// @file Composable that returns the virtual keyboard height (roughly accurate)
// Known to not work on Android app but works on Android web browsers ¯\_(ツ)_/¯
import { useWindowSizeStore } from '@@/pinia/window_size'
import { createSharedComposable, until } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, nextTick } from 'vue'

export const useVirtualKeyboardHeight = createSharedComposable(
  (): {
    isVirtualKeyboardShowing: typeof isVirtualKeyboardShowing
    virtualKeyboardHeight: typeof virtualKeyboardHeight
    awaitVirtualKeyboardHidden: typeof awaitVirtualKeyboardHidden
  } => {
    const { windowHeight, visualViewportHeight, visualViewportScale } = storeToRefs(useWindowSizeStore())

    const actualVisualViewportHeight = computed(() =>
      Math.round(visualViewportHeight.value * visualViewportScale.value),
    )
    const isVirtualKeyboardShowing = computed(() => actualVisualViewportHeight.value < windowHeight.value)
    const virtualKeyboardHeight = computed(() =>
      isVirtualKeyboardShowing.value ? windowHeight.value - actualVisualViewportHeight.value : 0,
    )

    /**
     * Resolves when the virtual keyboard is hidden or when the next tick is done,
     * whichever comes last.
     */
    const awaitVirtualKeyboardHidden = async (): Promise<void> => {
      const virtualKeyboardHiddenPromise = new Promise<void>((resolve) => {
        if (isVirtualKeyboardShowing.value) {
          void until(isVirtualKeyboardShowing)
            .toBe(false)
            .then(() => resolve())
        } else {
          resolve()
        }
      })
      await Promise.allSettled([virtualKeyboardHiddenPromise, nextTick])
    }

    return {
      isVirtualKeyboardShowing,
      virtualKeyboardHeight,
      awaitVirtualKeyboardHidden,
    }
  },
)
